import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Pencil, Pin, PinOff, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, PickSelect } from "@/components/apply/Field";
import { RichEditor } from "@/components/RichEditor";
import { PageTitle } from "@/pages/admin/AdminLayout";
import { api, fmtDate, formatErr } from "@/lib/api";

const EMPTY = { title: "", body: "", visibility: "public", status: "published", publish_at: "", pinned: false };
const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const AnnDialog = ({ ann, onClose, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...ann, publish_at: toLocal(ann.publish_at) });
  const [image, setImage] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    const fd = new FormData();
    ["title", "body", "visibility", "status"].forEach((k) => fd.append(k, f[k]));
    fd.append("publish_at", f.publish_at ? new Date(f.publish_at).toISOString() : "");
    fd.append("pinned", f.pinned ? "true" : "false");
    if (image) fd.append("image", image);
    if (ann.id) fd.append("remove_image", removeImage ? "true" : "false");
    try {
      if (ann.id) await api.put(`/admin/announcements/${ann.id}`, fd);
      else await api.post("/admin/announcements", fd);
      toast.success("Announcement saved");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="announcement-dialog">
        <DialogHeader><DialogTitle>{ann.id ? "Edit announcement" : "New announcement"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input placeholder="Title" value={f.title} onChange={set("title")} className="h-11 rounded-xl" data-testid="announcement-title-input" />
          <RichEditor value={f.body} onChange={set("body")} testId="announcement-editor" minHeight={200} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Visibility"><PickSelect value={f.visibility} onChange={set("visibility")} options={[{ value: "public", label: "Public (home + Announcements page)" }, { value: "members", label: "Members only (member portal)" }]} testId="announcement-visibility-select" /></Field>
            <Field label="Status"><PickSelect value={f.status} onChange={set("status")} options={[{ value: "published", label: "Published" }, { value: "draft", label: "Draft / unpublished" }]} testId="announcement-status-select" /></Field>
            <Field label="Publish date & time" hint="Leave blank to publish now. Future dates schedule the post."><Input type="datetime-local" value={f.publish_at} onChange={set("publish_at")} className="h-11 rounded-xl" data-testid="announcement-publish-at-input" /></Field>
            <label className="flex items-center justify-between rounded-xl border border-border p-3 self-end"><span className="text-sm font-bold">Pin to top</span><Switch checked={f.pinned} onCheckedChange={set("pinned")} data-testid="announcement-pinned-toggle" /></label>
          </div>
          <Field label="Cover image" optional>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => setImage(e.target.files?.[0] || null)} className="text-sm" data-testid="announcement-image-input" />
            {ann.has_image && !image && <label className="flex items-center gap-2 text-sm mt-2"><Switch checked={removeImage} onCheckedChange={setRemoveImage} data-testid="announcement-remove-image-toggle" />Remove current image</label>}
          </Field>
          <button onClick={save} className="w-full h-11 rounded-full bg-primary text-primary-foreground font-bold" data-testid="announcement-save-button">Save announcement</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const stateLabel = (a) => (a.status === "draft" ? "Draft" : a.publish_at > new Date().toISOString() ? "Scheduled" : "Live");

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/announcements").then((r) => setItems(r.data)).catch((e) => toast.error(formatErr(e))), []);
  useEffect(() => {
    load();
  }, [load]);
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div data-testid="admin-announcements-page">
      <PageTitle title="Announcements" sub="Public posts appear on the home page and Announcements page. Members-only posts appear in the member portal.">
        <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex h-10 items-center gap-2 px-5 rounded-full bg-primary text-primary-foreground font-bold text-sm" data-testid="add-announcement-button"><Plus className="h-4 w-4" />New announcement</button>
      </PageTitle>
      {editing && <AnnDialog ann={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {items.length === 0 && <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="admin-announcements-empty">No announcements yet.</div>}
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-center gap-4" data-testid={`admin-announcement-${a.id}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="font-bold flex items-center gap-2">{a.pinned && <Pin className="h-4 w-4 text-amber-500" />}{a.title}</p>
              <p className="text-xs text-muted-foreground">{a.visibility === "public" ? "Public" : "Members only"} · <span className="font-bold" data-testid={`admin-announcement-state-${a.id}`}>{stateLabel(a)}</span> · {fmtDate(a.publish_at)}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEditing(a)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-secondary" aria-label="Edit" data-testid={`announcement-edit-${a.id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => act(() => api.patch(`/admin/announcements/${a.id}`, { pinned: !a.pinned }), a.pinned ? "Unpinned" : "Pinned")} className="h-9 w-9 grid place-items-center rounded-full hover:bg-secondary" aria-label="Pin" data-testid={`announcement-pin-${a.id}`}>{a.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}</button>
              <button onClick={() => act(() => api.patch(`/admin/announcements/${a.id}`, { status: a.status === "draft" ? "published" : "draft" }), a.status === "draft" ? "Published" : "Unpublished")} className="h-9 w-9 grid place-items-center rounded-full hover:bg-secondary" aria-label="Publish toggle" data-testid={`announcement-publish-toggle-${a.id}`}>{a.status === "draft" ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
              <button onClick={() => window.confirm("Delete this announcement?") && act(() => api.delete(`/admin/announcements/${a.id}`), "Deleted")} className="h-9 w-9 grid place-items-center rounded-full hover:bg-secondary" aria-label="Delete" data-testid={`announcement-delete-${a.id}`}><Trash2 className="h-4 w-4 text-destructive" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
